// src/components/game/tasks/ProfessionMechanics.jsx
import React, { useState } from 'react';

// ==========================================
// 1. BANKING: THE TRUST LEDGER
// ==========================================
export function BankingLedger({ onComplete }) {
  const [trust, setTrust] = useState(50);
  const [step, setStep] = useState(0);

  const transactions = [
    { id: 1, client: "Shell Co. Alpha", request: "Process $200k transfer without KYC.", bribe: 5000, risk: "High" },
    { id: 2, client: "Local Charity", request: "Expedite grant release (No profit).", bribe: 0, risk: "None" },
    { id: 3, client: "Oligarch's Nephew", request: "Approve unsecured loan.", bribe: 2000, risk: "Medium" }
  ];

  const handleDecision = (tx, action) => {
    if (action === 'approve') {
      if (tx.risk === 'High') setTrust(t => t - 20);
      else setTrust(t => t + 10);
    } else {
      if (tx.risk === 'High') setTrust(t => t + 10);
    }

    if (step + 1 < transactions.length) setStep(step + 1);
    else onComplete(trust);
  };

  const tx = transactions[step];

  return (
    <div className="bg-gray-900 border-2 border-green-500 p-6 rounded-lg max-w-2xl w-full">
      <div className="flex justify-between mb-6 border-b border-gray-700 pb-2">
        <h3 className="text-xl text-green-400 font-mono">TRUST LEDGER</h3>
        <div className="text-right text-xs text-gray-400">Public Trust: <span className={trust<40?'text-red-500':'text-blue-400'}>{trust}%</span></div>
      </div>
      <div className="bg-black p-4 mb-6 font-mono text-sm text-green-300">
        <p>REQ_ID: {tx.id}</p><p>CLIENT: {tx.client}</p><p>ACTION: {tx.request}</p>
        <p className="text-yellow-500 mt-2">POTENTIAL GAIN: ${tx.bribe}</p>
      </div>
      <div className="flex gap-4">
        <button onClick={() => handleDecision(tx, 'approve')} className="flex-1 bg-green-700 hover:bg-green-600 text-white py-3 rounded font-bold">APPROVE</button>
        <button onClick={() => handleDecision(tx, 'reject')} className="flex-1 bg-red-700 hover:bg-red-600 text-white py-3 rounded font-bold">REJECT</button>
      </div>
    </div>
  );
}

// ==========================================
// 2. MEDICAL: TRIAGE PROTOCOL
// ==========================================
export function MedicalTriage({ onComplete }) {
  return (
    <div className="bg-gray-900 border-2 border-blue-500 p-6 rounded-lg max-w-2xl w-full">
      <h3 className="text-xl text-blue-400 font-bold mb-4">CRITICAL DECISION: TRIAGE</h3>
      <div className="bg-gray-800 p-4 rounded mb-6 text-gray-300">
        <p className="mb-2"><strong>Patient A:</strong> 45yo Official. Minor symptoms. Demands ventilator.</p>
        <p><strong>Patient B:</strong> 12yo Child. Critical. Needs ventilator immediately.</p>
        <p className="mt-4 italic text-red-400">Resources: 1 Ventilator.</p>
      </div>
      <div className="grid gap-3">
        <button onClick={() => onComplete(100)} className="w-full text-left p-4 bg-gray-700 hover:bg-blue-600 rounded border border-gray-600">Save Patient B (The Child). Risk political backlash.</button>
        <button onClick={() => onComplete(20)} className="w-full text-left p-4 bg-gray-700 hover:bg-red-900 rounded border border-gray-600">Save Patient A (The Official). Secure funding.</button>
      </div>
    </div>
  );
}

// ==========================================
// 3. LOGISTICS: ROUTE WEAVING
// ==========================================
export function LogisticsRouter({ onComplete }) {
  return (
    <div className="bg-gray-900 border-2 border-yellow-500 p-6 rounded-lg max-w-2xl w-full">
      <h3 className="text-xl text-yellow-400 font-bold mb-4">SUPPLY CHAIN DISRUPTION</h3>
      <p className="text-white mb-6">Highway blocked by protests. Urgent medical cargo.</p>
      <div className="grid gap-3">
        <button onClick={() => onComplete(80)} className="p-3 border border-yellow-600 rounded hover:bg-yellow-900 text-white">Route via Mountain Pass (High Risk, On Time)</button>
        <button onClick={() => onComplete(50)} className="p-3 border border-yellow-600 rounded hover:bg-yellow-900 text-white">Delay Shipment (Safe, Late Penalty)</button>
        <button onClick={() => onComplete(0)} className="p-3 border border-yellow-600 rounded hover:bg-yellow-900 text-white">Bribe Protest Leaders (Illegal)</button>
      </div>
    </div>
  );
}

// ==========================================
// 4. MINING: GEOLOGICAL MEMORY
// ==========================================
export function MiningScanner({ onComplete }) {
  const [stability, setStability] = useState(100);
  const [ore, setOre] = useState(0);
  
  const drill = (type) => {
      if (type === 'aggressive') { setOre(ore + 50); setStability(stability - 40); }
      else { setOre(ore + 20); setStability(stability - 10); }
  };

  if (stability <= 0) return <div className="bg-red-900 p-6 rounded text-white text-center"><h2>COLLAPSE DETECTED!</h2><button onClick={() => onComplete(0)} className="mt-4 bg-black px-4 py-2 rounded">Evacuate</button></div>;
  if (ore >= 100) return <div className="bg-green-900 p-6 rounded text-white text-center"><h2>QUOTA MET</h2><button onClick={() => onComplete(100)} className="mt-4 bg-black px-4 py-2 rounded">Finish Shift</button></div>;

  return (
    <div className="bg-gray-900 border-2 border-orange-700 p-6 rounded-lg max-w-2xl w-full">
        <h3 className="text-xl text-orange-500 font-mono mb-4">GEOLOGICAL SENSOR</h3>
        <div className="grid grid-cols-2 gap-4 mb-6 text-center">
            <div className="bg-black p-2 rounded border border-orange-900"><div className="text-xs text-gray-500">STABILITY</div><div className={`text-2xl ${stability<40?'text-red-500':'text-green-500'}`}>{stability}%</div></div>
            <div className="bg-black p-2 rounded border border-orange-900"><div className="text-xs text-gray-500">ORE EXTRACTED</div><div className="text-2xl text-yellow-500">{ore}/100</div></div>
        </div>
        <div className="grid gap-3">
            <button onClick={() => drill('careful')} className="p-3 bg-gray-800 hover:bg-gray-700 rounded border border-gray-600 text-white">Careful Extraction (+20 Ore, -10 Stable)</button>
            <button onClick={() => drill('aggressive')} className="p-3 bg-gray-800 hover:bg-red-900/50 rounded border border-red-900 text-white">Fracking Blast (+50 Ore, -40 Stable)</button>
        </div>
    </div>
  );
}

// ==========================================
// 5. OIL & GAS: PRESSURE GRADIENT
// ==========================================
export function PressureControl({ onComplete }) {
    const [pressure, setPressure] = useState(50);
    const [flow, setFlow] = useState(0);

    const adjust = (amount) => {
        const newP = pressure + amount;
        setPressure(newP);
        setFlow(flow + (newP > 80 ? 100 : 50));
        if (newP > 95) onComplete(0); // Explosion
        if (flow > 300) onComplete(100); // Success
    };

    return (
        <div className="bg-gray-900 border-2 border-red-600 p-6 rounded-lg max-w-2xl w-full">
            <h3 className="text-xl text-red-500 font-mono mb-4">PIPELINE PRESSURE</h3>
            <div className="w-full bg-gray-700 h-6 rounded-full mb-6 relative overflow-hidden">
                <div className={`h-full transition-all duration-300 ${pressure>80?'bg-red-600':'bg-green-500'}`} style={{width: `${pressure}%`}}></div>
            </div>
            <p className="text-center text-gray-300 mb-4">Output: {flow}/300 Barrels</p>
            <div className="flex gap-4">
                <button onClick={() => adjust(-10)} className="flex-1 py-3 bg-blue-900 hover:bg-blue-800 rounded text-white">Relieve Pressure</button>
                <button onClick={() => adjust(15)} className="flex-1 py-3 bg-red-900 hover:bg-red-800 rounded text-white">Boost Flow (Risk)</button>
            </div>
        </div>
    );
}

// ==========================================
// 6. PILOT & ATC: CLEARANCE SYNTAX
// ==========================================
export function FlightClearance({ onComplete }) {
    return (
        <div className="bg-gray-900 border-2 border-cyan-500 p-6 rounded-lg max-w-2xl w-full">
            <h3 className="text-xl text-cyan-400 font-mono mb-4">ATC DATA LINK</h3>
            <p className="bg-black p-4 text-green-400 font-mono mb-6">"MAYDAY. GPS Failure. Requesting vector to nearest safe corridor."</p>
            <div className="grid gap-3">
                <button onClick={() => onComplete(100)} className="text-left p-3 border border-cyan-700 hover:bg-cyan-900 rounded text-white">
                    "Maintain FL350. Turn Right Heading 270. Visual Approach." (Standard)
                </button>
                <button onClick={() => onComplete(50)} className="text-left p-3 border border-cyan-700 hover:bg-cyan-900 rounded text-white">
                    "Descend immediately to 5000ft." (Risky in mountains)
                </button>
                <button onClick={() => onComplete(0)} className="text-left p-3 border border-cyan-700 hover:bg-cyan-900 rounded text-white">
                    "Use secondary GPS frequency." (Spoofed - Crash Risk)
                </button>
            </div>
        </div>
    );
}

// ==========================================
// 7. AESTHETICIANS: SURFACE SEMIOTICS
// ==========================================
export function FaceScrambler({ onComplete }) {
    return (
        <div className="bg-gray-900 border-2 border-pink-500 p-6 rounded-lg max-w-2xl w-full">
            <h3 className="text-xl text-pink-400 font-bold mb-4">CLIENT: DISSIDENT LEADER</h3>
            <p className="text-white mb-6">"I need to pass the AI checkpoints. Make me invisible."</p>
            <div className="grid gap-3">
                <button onClick={() => onComplete(100)} className="p-3 border border-pink-700 hover:bg-pink-900 rounded text-white">
                    Apply CV Dazzle Pattern (Anti-Surveillance Makeup)
                </button>
                <button onClick={() => onComplete(60)} className="p-3 border border-pink-700 hover:bg-pink-900 rounded text-white">
                    Standard Glamour (High Status Disguise)
                </button>
                <button onClick={() => onComplete(20)} className="p-3 border border-pink-700 hover:bg-pink-900 rounded text-white">
                    Surgical Alteration (Permanent & Risky)
                </button>
            </div>
        </div>
    );
}

// ==========================================
// 8. LEGAL: CLAUSE GARDENING
// ==========================================
export function ContractDraft({ onComplete }) {
    return (
        <div className="bg-gray-900 border-2 border-gray-400 p-6 rounded-lg max-w-2xl w-full">
            <h3 className="text-xl text-white font-serif mb-4">DRAFTING: REFUGEE STATUS</h3>
            <p className="text-gray-300 mb-6">Define "Political Persecution" for the new tribunal.</p>
            <div className="grid gap-3 font-serif">
                <button onClick={() => onComplete(100)} className="p-4 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-white">
                    "Any systemic threat to life or liberty based on belief." (Broad/Ethical)
                </button>
                <button onClick={() => onComplete(50)} className="p-4 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-white">
                    "Documented violence by state actors only." (Narrow/Safe)
                </button>
                <button onClick={() => onComplete(0)} className="p-4 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-white">
                    "Determined by current economic utility." (Corrupt)
                </button>
            </div>
        </div>
    );
}

// ==========================================
// 9. HOSPITALITY: THRESHOLD RITUALS
// ==========================================
export function GuestWelcome({ onComplete }) {
    return (
        <div className="bg-gray-900 border-2 border-purple-500 p-6 rounded-lg max-w-2xl w-full">
            <h3 className="text-xl text-purple-400 font-bold mb-4">GUEST ARRIVAL: THE GENERAL</h3>
            <p className="text-white mb-6">A high-value target checks into the Safe House. He is nervous.</p>
            <div className="grid gap-3">
                <button onClick={() => onComplete(80)} className="p-3 border border-purple-700 hover:bg-purple-900 rounded text-white">
                    Offer "The Usual" (Discrete recognition of status)
                </button>
                <button onClick={() => onComplete(100)} className="p-3 border border-purple-700 hover:bg-purple-900 rounded text-white">
                    Trigger Silent Mode (Disable room sensors & logs)
                </button>
                <button onClick={() => onComplete(20)} className="p-3 border border-purple-700 hover:bg-purple-900 rounded text-white">
                    Ask for ID Verification (Standard Protocol)
                </button>
            </div>
        </div>
    );
}

// ==========================================
// 10. CABIN CREW: ATMOSPHERE CONTROL
// ==========================================
export function CabinPanic({ onComplete }) {
    const [panic, setPanic] = useState(50);
    
    const act = (type) => {
        if (type === 'calm') setPanic(panic - 20);
        if (type === 'command') setPanic(panic - 10);
        if (type === 'ignore') setPanic(panic + 10);
        
        if (panic <= 20) onComplete(100); // Safe
        if (panic >= 90) onComplete(0); // Riot
    };

    return (
        <div className="bg-gray-900 border-2 border-indigo-500 p-6 rounded-lg max-w-2xl w-full">
            <h3 className="text-xl text-indigo-400 font-bold mb-4">TURBULENCE & PANIC</h3>
            <div className="w-full bg-gray-700 h-4 rounded-full mb-6">
                <div className={`h-full ${panic>60?'bg-red-500':'bg-blue-500'}`} style={{width: `${panic}%`}}></div>
            </div>
            <p className="text-center text-gray-400 mb-4">Panic Level: {panic}%</p>
            <div className="grid grid-cols-3 gap-2">
                <button onClick={() => act('calm')} className="p-3 bg-indigo-900 hover:bg-indigo-800 rounded text-sm text-white">Reassure</button>
                <button onClick={() => act('command')} className="p-3 bg-blue-900 hover:bg-blue-800 rounded text-sm text-white">Command Sit</button>
                <button onClick={() => act('ignore')} className="p-3 bg-gray-800 hover:bg-gray-700 rounded text-sm text-white">Focus on Service</button>
            </div>
        </div>
    );
}

// --- MASTER ROUTER ---
export default function ProfessionTask({ profession, onComplete }) {
    // Normalize string to match case
    const p = profession || "Logistics";
    
    // Robust Matching Logic (Handles "Oil & Gas" vs "OilGas")
    if (p.includes("Bank")) return <BankingLedger onComplete={onComplete} />;
    if (p.includes("Medic")) return <MedicalTriage onComplete={onComplete} />;
    if (p.includes("Logist")) return <LogisticsRouter onComplete={onComplete} />;
    if (p.includes("Mining")) return <MiningScanner onComplete={onComplete} />;
    if (p.includes("Oil") || p.includes("Gas")) return <PressureControl onComplete={onComplete} />;
    if (p.includes("Pilot") || p.includes("Aviation") || p.includes("ATC")) return <FlightClearance onComplete={onComplete} />;
    if (p.includes("Aesthet") || p.includes("Beauty")) return <FaceScrambler onComplete={onComplete} />;
    if (p.includes("Legal") || p.includes("Law")) return <ContractDraft onComplete={onComplete} />;
    if (p.includes("Hosp") || p.includes("Hotel")) return <GuestWelcome onComplete={onComplete} />;
    if (p.includes("Cabin") || p.includes("Crew")) return <CabinPanic onComplete={onComplete} />;

    // Fallback if no match found
    return (
        <div className="p-6 bg-gray-800 rounded border border-gray-600">
            <h3 className="text-white mb-4">Standard Protocol</h3>
            <p className="text-gray-400 mb-6">Execute standard operating procedure for {profession}.</p>
            <button onClick={() => onComplete(50)} className="bg-blue-600 text-white px-6 py-2 rounded">Proceed</button>
        </div>
    );
}